import { AnimatePresence } from 'framer-motion'
import { useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import {
  useCurrentRefinements,
  useInfiniteHits,
  useInstantSearch,
} from 'react-instantsearch'
import { useInView } from 'react-intersection-observer'
import { isEmpty, isNonNullish } from 'remeda'
import { useMediaQuery } from 'usehooks-ts'
import { useHeader } from '@velasca/hooks/use-header'
import type { AttributeForFacetingConfig } from '@velasca/types'
import { scrollTo } from '@velasca/utils/scroll-to'
import { ActiveRefinements } from './ActiveRefinements'
import { useAlgoliaFilters } from './AlgoliaSearchProvider'
import { Facets } from './Facets'
import { FacetsDrawer } from './FacetsDrawer'
import {
  Container,
  DesktopFilterButtonContainer,
  FilterIcon,
  FiltersHeader,
  OpenFilterButton,
  RightSide,
} from './styles'

const FILTERS_PANEL_CONTAINER_ID = 'filters-panel-container'

const categoryWrapper = document.querySelector<HTMLElement>(
  '[data-products-count]',
)

const mobileModalEl = document.querySelector<HTMLElement>('.mobile-modal')

export const AlgoliaFilters = () => {
  const { headerHeight } = useHeader()
  const navbarHeight = useHeader((state) => state.navbarHeight)
  const promotionalBannerHeight = useHeader(
    (state) => state.promotionalBannerHeight,
  )

  /*
    I calc navbarHeight + promotionalBannerHeight because
    headerHeight includes helpMenuHeight and pushes the button
    too far away the header
  */
  const { canRefine, items } = useCurrentRefinements()
  const { setIsMenuOpen, setIsSearchActive } = useAlgoliaFilters()
  const { t } = useTranslation('collection-filters')
  const isDesktopViewport = useMediaQuery('(min-width: 992px)')
  const [filtersPanelContainerRef, isFiltersPanelContainerInView] = useInView({
    initialInView: true,
  })
  const [topOfPageRef, isTopOfPageInView] = useInView({
    rootMargin: `-${headerHeight}px 0px 0px 0px`,
  })
  const { results } = useInfiniteHits()
  const { status } = useInstantSearch()
  const headerWithoutHelpMenuHeight = useMemo(
    () => navbarHeight + promotionalBannerHeight,
    [navbarHeight, promotionalBannerHeight],
  )

  useEffect(() => {
    if (categoryWrapper === null) {
      return
    }

    if (canRefine) {
      categoryWrapper.style.minHeight = 'auto'
    } else {
      categoryWrapper.removeAttribute('style')
    }
  }, [canRefine])

  const attributesForFaceting: AttributeForFacetingConfig[] = [
    {
      attribute: 'colors_filter',
      locale: 'filterColor',
      handle: 'color',
    },
    {
      attribute: 'materials_filter',
      locale: 'filterMaterial',
      handle: 'material',
      limit: 100,
    },
    {
      attribute: 'available_sizes_object',
      locale: 'filterSize',
      handle: 'size',
      limit: 100,
    },
    {
      attribute: 'product_type_filter',
      locale: 'filterModel',
      handle: 'model',
      limit: 100,
    },
    {
      attribute: 'season_name',
      locale: 'filterSeason',
      handle: 'season',
      limit: 100,
    },
    {
      attribute: 'collections.handle',
      locale: 'filterCategories',
      handle: 'categories',
      limit: 100,
    },
    {
      attribute: 'soles_filter',
      locale: 'filterSole',
      handle: 'sole',
      limit: 100,
    },
  ]

  return (
    <>
      <span ref={topOfPageRef}></span>
      <Container
        topDistance={headerWithoutHelpMenuHeight}
        ref={filtersPanelContainerRef}
        id={FILTERS_PANEL_CONTAINER_ID}
        isSticky={!isTopOfPageInView}
      >
        {!isDesktopViewport && (
          <FiltersHeader>
            <OpenFilterButton
              onClick={() => {
                setIsMenuOpen(true)
                setIsSearchActive(true)
              }}
            >
              <FilterIcon />
              {t('filterBy')}
            </OpenFilterButton>
            {!isEmpty(items) &&
              isNonNullish(results?.nbHits) &&
              status === 'idle' && (
                <RightSide>
                  {t('results', { count: results?.nbHits })}
                </RightSide>
              )}
          </FiltersHeader>
        )}
        {isDesktopViewport && <Facets panels={attributesForFaceting} />}
        <ActiveRefinements isSticky={!isTopOfPageInView} />
        {!isDesktopViewport &&
          mobileModalEl &&
          createPortal(
            <FacetsDrawer panels={attributesForFaceting} />,
            mobileModalEl,
          )}
      </Container>
      {isDesktopViewport && (
        <AnimatePresence>
          {!isFiltersPanelContainerInView && (
            <DesktopFilterButtonContainer
              topDistance={headerWithoutHelpMenuHeight}
              initial={{ opacity: 0, y: -headerWithoutHelpMenuHeight }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.1, ease: 'easeInOut' }}
              exit={{ opacity: 0, y: -headerWithoutHelpMenuHeight }}
            >
              <OpenFilterButton
                onClick={() => {
                  scrollTo(
                    `#${FILTERS_PANEL_CONTAINER_ID}`,
                    headerWithoutHelpMenuHeight,
                  )
                }}
              >
                <FilterIcon />
                {t('filterBy')}
              </OpenFilterButton>
            </DesktopFilterButtonContainer>
          )}
        </AnimatePresence>
      )}
    </>
  )
}
